import { useId } from "react";
import { Link } from "@tanstack/react-router";
import { buildWatchList, addDays, type WatchItem } from "@/lib/onset";
import { getIdProfile } from "@/lib/tick-id";
import { WarningCard } from "@/components/page-shell";
import { cn } from "@/lib/utils";

function fmt(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The window as two lines for the date column: calendar dates, then bite-day numbers. */
function windowParts(item: WatchItem, bite: Date): { dates: string; days?: string } {
  if (item.opensOnDay === undefined && item.closesOnDay === undefined) {
    return { dates: "No published window" };
  }
  if (item.opensOnDay === undefined) {
    return {
      dates: `Through ${fmt(addDays(bite, item.closesOnDay!))}`,
      days: `up to day ${item.closesOnDay}`,
    };
  }
  if (item.closesOnDay === undefined) {
    return {
      dates: `From ${fmt(addDays(bite, item.opensOnDay))}`,
      days: `from day ${item.opensOnDay}`,
    };
  }
  return {
    dates: `${fmt(addDays(bite, item.opensOnDay))} – ${fmt(addDays(bite, item.closesOnDay))}`,
    days: `days ${item.opensOnDay}–${item.closesOnDay}`,
  };
}

/**
 * A species name for the middle of a sentence: "Blacklegged tick" -> "blacklegged tick",
 * but "Lone Star tick" and "American dog tick" keep the capital their place name needs.
 */
function inSentence(name: string): string {
  if (/^(Lone Star|American|Asian|Gulf Coast|Rocky Mountain)\b/.test(name)) return name;
  return name.charAt(0).toLowerCase() + name.slice(1);
}

type Tone = "active" | "upcoming" | "passed";

/**
 * The symptom-watch timeline.
 *
 * This is the first thing on the site that reads the bite log back. It restates the
 * sourced onset windows in src/lib/onset.ts against a date the person typed in — it
 * does not assess them, score them, or suggest what they have. A window closing is
 * reported as the published window closing and nothing more, and windows the sources
 * treat as open-ended never show as passed at all.
 *
 * Set as a ruled table, dates in the left column, the way a printed schedule reads.
 * Colour is used once: the rust rule over what is open now.
 */
export function WatchTimeline({
  bite,
  today = new Date(),
  speciesSlug,
}: {
  bite: Date;
  today?: Date;
  speciesSlug?: string;
}) {
  const profile = speciesSlug ? getIdProfile(speciesSlug) : undefined;
  // Only narrow when we know the tick AND it has published illnesses. A species with an
  // empty list means "nothing on this site", not "nothing to watch for".
  const slugs = profile && profile.diseaseSlugs.length > 0 ? profile.diseaseSlugs : undefined;
  const { items, dayNumber } = buildWatchList(bite, today, slugs);

  const active = items.filter((i) => i.active);
  const upcoming = items.filter((i) => !i.active && !i.passed);
  const passed = items.filter((i) => i.passed);

  return (
    <div className="space-y-10">
      <header className="break-inside-avoid break-after-avoid">
        <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
          {dayNumber === 0
            ? "Today is day 0 since the bite."
            : dayNumber < 0
              ? "That date is in the future. Check the date you entered."
              : `It has been ${dayNumber} ${dayNumber === 1 ? "day" : "days"} since the bite.`}
        </h2>
        <p className="measure mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">
          {profile
            ? `Showing the published windows for the ${inSentence(profile.commonName)}.`
            : "Showing every published window on this site. Tell us which tick it was and this list gets shorter."}
        </p>
      </header>

      {active.length > 0 && (
        <TimelineGroup title="Watch for these now" tone="active" items={active} bite={bite} />
      )}
      {upcoming.length > 0 && (
        <TimelineGroup
          title="Not yet: these windows open later"
          tone="upcoming"
          items={upcoming}
          bite={bite}
        />
      )}
      {passed.length > 0 && (
        <TimelineGroup
          title="These published windows have closed"
          tone="passed"
          items={passed}
          bite={bite}
        />
      )}

      <WarningCard>
        A closed window means the period described in the published source has passed, not that you
        are in the clear, and not that you are unwell if something appears later. Most tick bites
        cause no illness at all. If you feel unwell at any point, tell a licensed healthcare
        professional that you were bitten and when.
      </WarningCard>
    </div>
  );
}

function TimelineGroup({
  title,
  tone,
  items,
  bite,
}: {
  title: string;
  tone: Tone;
  items: WatchItem[];
  bite: Date;
}) {
  const id = useId();
  const passed = tone === "passed";
  return (
    <section aria-labelledby={id}>
      <h3
        id={id}
        className={cn(
          "font-display text-xl font-medium break-after-avoid",
          passed ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {title}
      </h3>
      <ul
        className={cn(
          "mt-3",
          tone === "active" ? "border-t-2 border-rust" : "border-t border-rule/70",
        )}
      >
        {items.map((item) => {
          const w = windowParts(item, bite);
          return (
            <li
              key={item.slug}
              className="grid grid-cols-[6.75rem_1fr] gap-x-4 border-b border-border py-3.5 sm:grid-cols-[10rem_1fr] sm:gap-x-6"
            >
              <p className="tabular pt-0.5 text-sm leading-snug">
                <span
                  className={cn(
                    "block",
                    tone === "active" && "font-semibold text-foreground",
                    tone === "upcoming" && "text-foreground",
                    passed && "text-muted-foreground",
                  )}
                >
                  {w.dates}
                </span>
                {w.days && <span className="block text-muted-foreground">{w.days}</span>}
              </p>
              <div className="min-w-0">
                <Link
                  to="/diseases/$disease"
                  params={{ disease: item.slug }}
                  className={cn(
                    "font-display text-lg font-medium leading-snug underline decoration-border underline-offset-4 transition-colors hover:text-primary hover:decoration-current",
                    passed ? "text-muted-foreground" : "text-foreground",
                  )}
                >
                  {item.shortName}
                </Link>
                <p
                  className={cn(
                    "mt-0.5 text-[0.9375rem] leading-relaxed",
                    passed ? "text-muted-foreground" : "text-foreground",
                  )}
                >
                  {item.watchFor}
                </p>
                {item.note && (
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {item.note}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
